/**
 * Pure helpers for admin analytics dashboards (no Supabase).
 */

export const DATE_PRESETS = {
  today: 'today',
  last7: 'last7',
  last30: 'last30',
  ytd: 'ytd',
  custom: 'custom',
};

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const endOfDay = (d) => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
};

export const getDateRangeForPreset = (preset, customStart, customEnd) => {
  const now = new Date();
  switch (preset) {
    case DATE_PRESETS.today:
      return { start: startOfDay(now), end: endOfDay(now) };
    case DATE_PRESETS.last7: {
      const start = startOfDay(now);
      start.setDate(start.getDate() - 6);
      return { start, end: endOfDay(now) };
    }
    case DATE_PRESETS.last30: {
      const start = startOfDay(now);
      start.setDate(start.getDate() - 29);
      return { start, end: endOfDay(now) };
    }
    case DATE_PRESETS.ytd: {
      const start = new Date(now.getFullYear(), 0, 1);
      return { start: startOfDay(start), end: endOfDay(now) };
    }
    case DATE_PRESETS.custom:
    default: {
      const start = customStart ? startOfDay(new Date(customStart)) : startOfDay(now);
      const end = customEnd ? endOfDay(new Date(customEnd)) : endOfDay(now);
      return { start, end };
    }
  }
};

export const toIsoRange = (start, end) => ({
  startIso: start.toISOString(),
  endIso: end.toISOString(),
});

export const formatDateKey = (date) => {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const parseDurationSeconds = (session) => {
  if (session.duration_seconds != null && !Number.isNaN(session.duration_seconds)) {
    return session.duration_seconds;
  }
  if (session.started_at && session.ended_at) {
    return Math.max(
      0,
      Math.floor((new Date(session.ended_at) - new Date(session.started_at)) / 1000)
    );
  }
  if (session.started_at && session.updated_at) {
    return Math.max(
      0,
      Math.floor((new Date(session.updated_at) - new Date(session.started_at)) / 1000)
    );
  }
  return null;
};

export const sessionPageCount = (session, pageViewsBySession) => {
  if (session.page_count != null && session.page_count > 0) {
    return session.page_count;
  }
  const views = pageViewsBySession.get(session.session_id);
  return views ? views.length : 0;
};

/** Build map session_id -> page view rows sorted by time */
export const indexPageViewsBySession = (pageViews) => {
  const map = new Map();
  (pageViews || []).forEach((pv) => {
    if (!map.has(pv.session_id)) map.set(pv.session_id, []);
    map.get(pv.session_id).push(pv);
  });
  map.forEach((list, key) => {
    list.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    map.set(key, list);
  });
  return map;
};

export const filterSessions = (sessions, pageViewsBySession, filters) => {
  const {
    minDurationSeconds = 0,
    maxDurationSeconds = null,
    minPages = 0,
    maxPages = null,
    pagePath = '',
  } = filters || {};

  return (sessions || []).filter((session) => {
    const duration = parseDurationSeconds(session);
    if (minDurationSeconds > 0) {
      if (duration == null || duration < minDurationSeconds) return false;
    }
    if (maxDurationSeconds != null && maxDurationSeconds > 0) {
      if (duration == null || duration > maxDurationSeconds) return false;
    }

    const pages = sessionPageCount(session, pageViewsBySession);
    if (minPages > 0 && pages < minPages) return false;
    if (maxPages != null && maxPages > 0 && pages > maxPages) return false;

    if (pagePath && pagePath.trim()) {
      const views = pageViewsBySession.get(session.session_id) || [];
      const needle = pagePath.trim().toLowerCase();
      const visited = views.some((v) => (v.page_path || '').toLowerCase().includes(needle));
      if (!visited) return false;
    }

    return true;
  });
};

/**
 * Stable visitor key for unique counts: logged-in user_id, else anonymous client_id.
 * IP is intentionally not used (shared NAT, VPN, mobile churn, heavier PII).
 */
export const visitorKey = (row) => {
  if (row?.user_id) return `user:${row.user_id}`;
  if (row?.client_id) return `client:${row.client_id}`;
  return null;
};

/** Unique visitors per calendar day (respecting filtered sessions) */
export const uniqueVisitorsByDay = (pageViews, allowedSessionIds) => {
  const byDay = new Map();
  (pageViews || []).forEach((pv) => {
    if (allowedSessionIds && !allowedSessionIds.has(pv.session_id)) return;
    const vKey = visitorKey(pv);
    if (!vKey) return;
    const key = formatDateKey(pv.created_at);
    if (!byDay.has(key)) byDay.set(key, new Set());
    byDay.get(key).add(vKey);
  });
  return [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, visitors]) => ({ date, uniqueVisitors: visitors.size }));
};

export const countUniqueVisitors = (pageViews, allowedSessionIds) => {
  const visitors = new Set();
  (pageViews || []).forEach((pv) => {
    if (allowedSessionIds && !allowedSessionIds.has(pv.session_id)) return;
    const vKey = visitorKey(pv);
    if (vKey) visitors.add(vKey);
  });
  return visitors.size;
};

export const signupsByDay = (users, rangeStart, rangeEnd) => {
  const byDay = new Map();
  (users || []).forEach((u) => {
    if (!u.created_at) return;
    const created = new Date(u.created_at);
    if (created < rangeStart || created > rangeEnd) return;
    const key = formatDateKey(created);
    byDay.set(key, (byDay.get(key) || 0) + 1);
  });
  return [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, count]) => ({ date, signups: count }));
};

/** Estimate seconds on each page from sequential views in a session */
export const estimateTimeOnPages = (pageViews, sessionEndIso) => {
  const sorted = [...(pageViews || [])].sort(
    (a, b) => new Date(a.created_at) - new Date(b.created_at)
  );
  const endFallback = sessionEndIso ? new Date(sessionEndIso) : new Date();

  return sorted.map((pv, i) => {
    const start = new Date(pv.created_at);
    const next = sorted[i + 1];
    const end = next ? new Date(next.created_at) : endFallback;
    const seconds = Math.max(0, Math.floor((end - start) / 1000));
    return {
      page_path: pv.page_path,
      viewed_at: pv.created_at,
      seconds_on_page: seconds,
    };
  });
};

export const formatDuration = (seconds) => {
  if (seconds == null || Number.isNaN(seconds)) return '—';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return s ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm ? `${h}h ${rm}m` : `${h}h`;
};

/** Median of numeric durations (seconds); resists outlier long idle tabs */
export const medianSeconds = (values) => {
  const nums = (values || []).filter((d) => d != null && !Number.isNaN(d)).sort((a, b) => a - b);
  if (!nums.length) return null;
  const mid = Math.floor(nums.length / 2);
  if (nums.length % 2 === 1) return nums[mid];
  return Math.round((nums[mid - 1] + nums[mid]) / 2);
};

export const medianSessionDurationSeconds = (sessions) => {
  const durations = (sessions || [])
    .map(parseDurationSeconds)
    .filter((d) => d != null);
  return medianSeconds(durations);
};

export const distinctPagePaths = (pageViews) => {
  const set = new Set();
  (pageViews || []).forEach((pv) => {
    if (pv.page_path) set.add(pv.page_path);
  });
  return [...set].sort();
};
